import { i18n } from '@askable/i18n';
import { StatusCodes } from 'http-status-codes';
import { useSearchParams } from 'next/navigation';
import { useEffect, useMemo, useState } from 'react';
import { QUERY_PARAM } from 'src/constants/constants';
import { UserType } from '@/constants/graphql-enums';
import { TypedStorage } from 'src/utils/TypedStorage';
import { useQuery } from 'urql';

import { AUTH_CONFIG } from '@/src/network/auth';
import { requestedLocale, setLangCookie, toLocale } from '@/utils/lang-utils';
import { formatFullName } from '@/utils/string-utils';

import { createContext } from '../createContext';

import { Viewer } from './data/Viewer.query';

import type { ResultOf } from '@/src/graphql';
import type { IntercomSettings } from '@intercom/messenger-js-sdk/dist/types';
import type { ReactElement, ReactNode } from 'react';

type ViewerContextStatus = 'authenticated' | 'unauthenticated' | 'unknown' | 'resolving';

type ViewerContextState = {
  status: ViewerContextStatus;
  type: UserType[];
  // Team name tied to booking for current askable live
  details?: ResultOf<typeof Viewer>['viewer'];
  fetchingDetails: boolean;
  updateViewerStatus: () => void;
  setViewerTeam: (teamId: string) => void;

  intercomSettings: IntercomSettings | undefined;
};

type ViewerContextContext = ViewerContextState;

const [ViewerContextProviderComp, useViewerContext] = createContext<ViewerContextContext>({
  name: 'ViewerContext',
});

const ViewerContextProvider = ({ children }: { children: ReactNode }): ReactElement => {
  const searchParams = useSearchParams();

  function initViewerTeamState(): null | string {
    if (typeof window === 'undefined') {
      return null;
    }

    return searchParams?.get(QUERY_PARAM.TEAM_ID) ?? TypedStorage.get('viewer_team_id') ?? null;
  }

  const [status, setStatus] = useState<ViewerContextState['status']>('unknown');
  const [viewerTeam, setViewerTeamState] = useState<string | null>(initViewerTeamState());

  useEffect(() => {
    const teamId = searchParams?.get(QUERY_PARAM.TEAM_ID);

    if (teamId) {
      setViewerTeam(teamId);
    }
  }, [searchParams]);

  // Resolve ConnectedTeam from the Kinde token's org (teamId: null), not a persisted
  // viewer_team_id. The backend authorizes against the token. For a researcher, an explicit
  // teamId takes a bookings-based access check that returns a stale team or 401s the team
  // they just switched into, so the app shows a team that does not match the token.
  // The clients app does the same and never passes a teamId.
  const [{ data: viewerDetailsData, fetching, error }, getViewer] = useQuery({
    query: Viewer,
    pause: true,
    variables: { teamId: null },
  });

  useEffect(() => {
    if (!fetching && !error && viewerDetailsData) {
      setStatus('authenticated');

      // Follow the profile language of the account. A `?lang=` request wins, so an embedded study
      // or a preview keeps the language that the caller asks for.
      const lang = requestedLocale ?? toLocale(viewerDetailsData.viewer?.settings?.language);

      if (lang) {
        void i18n.changeLanguage(lang);
        setLangCookie(lang);
      }
    }
  }, [viewerDetailsData, error, fetching]);

  const updateViewerStatus = () => {
    const token = AUTH_CONFIG.getToken();

    if (!token || typeof token !== 'string') {
      setStatus('unauthenticated');
      return;
    }

    setStatus('resolving');
    getViewer();
  };

  useEffect(() => {
    updateViewerStatus();
  }, []);

  useEffect(() => {
    if (!viewerDetailsData?.viewer?._id) {
      return;
    }

    if (!viewerTeam && viewerDetailsData.viewer._default_team) {
      setViewerTeam(viewerDetailsData.viewer._default_team);
    }
  }, [viewerDetailsData]);

  const type = (() => {
    const types: UserType[] = [];

    if (viewerDetailsData?.viewer?.type?.researcher) {
      types.push(UserType.Researcher);
    }

    if (viewerDetailsData?.viewer?.type?.client) {
      types.push(UserType.Client);
    }

    if (viewerDetailsData?.viewer?.type?.participant) {
      types.push(UserType.Participant);
    }

    return types;
  })();

  const intercomSettings: IntercomSettings | undefined = useMemo(() => {
    if (status === 'unknown' || status === 'resolving' || !process.env.NEXT_PUBLIC_INTERCOM_APP_ID) {
      return undefined;
    }

    if (viewerDetailsData?.viewer && viewerDetailsData.viewer.intercomJWT) {
      return {
        app_id: process.env.NEXT_PUBLIC_INTERCOM_APP_ID,
        // Set base to US region to match the workspace (app ID).
        // Hitting a regional fallback can result in "App not found".
        api_base: 'https://api-iam.intercom.io',
        intercom_user_jwt: viewerDetailsData.viewer.intercomJWT,
        name: formatFullName(
          viewerDetailsData.viewer.meta?.identity?.firstname ?? '',
          viewerDetailsData?.viewer.meta?.identity?.lastname ?? '',
        ),
        Company: viewerDetailsData?.viewer.ConnectedTeam?.name,
        onPage: window.location.pathname,
        phone: viewerDetailsData.viewer.contact?.phone?.mobile ?? undefined,
        user_type: type ? type[0] : undefined,
        hide_default_launcher: true,
      };
    } else {
      return undefined;
    }
  }, [status, viewerDetailsData, type]);

  const setViewerTeam = (teamId: string) => {
    TypedStorage.set({ key: 'viewer_team_id', value: teamId });
    setViewerTeamState(teamId);
  };

  // If URL team id doesn't return a team (eg, not found, access denied), clear the team id from local storage
  useEffect(() => {
    if (viewerTeam && viewerDetailsData?.viewer?._id && !viewerDetailsData?.viewer?.ConnectedTeam?._id) {
      TypedStorage.remove('viewer_team_id');

      if (viewerDetailsData.viewer._default_team) {
        setViewerTeam(viewerDetailsData.viewer._default_team);
      } else {
        setViewerTeamState(null);
      }
    }
  }, [viewerTeam, viewerDetailsData?.viewer]);

  useEffect(() => {
    if (
      error?.graphQLErrors.some(e => e.extensions.code === StatusCodes.UNAUTHORIZED) ||
      error?.message.includes('jwt expired') ||
      error?.message.includes('invalid token')
    ) {
      TypedStorage.remove('viewer_jwt');
      setStatus('unauthenticated');
    }
  }, [error]);

  return (
    <ViewerContextProviderComp
      value={{
        type,
        details: viewerDetailsData?.viewer,
        fetchingDetails: fetching,
        status: fetching ? 'unknown' : status,
        updateViewerStatus,
        setViewerTeam,
        intercomSettings,
      }}
    >
      {children}
    </ViewerContextProviderComp>
  );
};

export { ViewerContextProvider, useViewerContext };
